import { t as translate, type Dictionary } from '@fsp/i18n';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from './api';

/**
 * Texts are written in their source language (Lithuanian for the panel, English for admin), exactly as
 * in the PHP app, so the dictionaries in @fsp/i18n apply unchanged. A dictionary is loaded only when
 * its language is chosen; until it arrives (or when a text is missing) the source text is shown.
 */

export const PANEL_LOCALES = { lt: 'Lietuvių', en: 'English', ru: 'Русский', tr: 'Türkçe', pl: 'Polski' } as const;
export const ADMIN_LOCALES = { en: 'English', lt: 'Lietuvių', tr: 'Türkçe' } as const;
export type Locale = keyof typeof PANEL_LOCALES;

const STORAGE_KEY = 'fsp-lang';

const DICTIONARIES: Record<string, () => Promise<{ default: Dictionary }>> = {
  'panel.en': () => import('@fsp/i18n/locales/panel.en.json'),
  'panel.ru': () => import('@fsp/i18n/locales/panel.ru.json'),
  'panel.tr': () => import('@fsp/i18n/locales/panel.tr.json'),
  'panel.pl': () => import('@fsp/i18n/locales/panel.pl.json'),
  'admin.lt': () => import('@fsp/i18n/locales/admin.lt.json'),
  'admin.tr': () => import('@fsp/i18n/locales/admin.tr.json'),
};

/** The language the site linked with (?lang=pl), else the one chosen before on this browser, else Lithuanian. */
function savedLocale(): Locale {
  const linked = new URLSearchParams(location.search).get('lang');
  if (linked && linked in PANEL_LOCALES) {
    try { localStorage.setItem(STORAGE_KEY, linked); } catch { /* private mode */ }
    return linked as Locale;
  }
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value && value in PANEL_LOCALES) return value as Locale;
  } catch { /* private mode */ }
  return 'lt';
}

interface I18n {
  locale: Locale;
  setLocale(locale: Locale): void;
  t(source: string, vars?: Record<string, string | number>): string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ context, children }: { context: 'panel' | 'admin'; children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(savedLocale);
  const [dictionary, setDictionary] = useState<Dictionary>({});
  useEffect(() => {
    let current = true;
    const load = DICTIONARIES[`${context}.${locale}`];
    if (!load) { setDictionary({}); return; } // source language
    load().then((module) => { if (current) setDictionary(module.default); }, () => { if (current) setDictionary({}); });
    return () => { current = false; };
  }, [context, locale]);
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    document.documentElement.lang = next;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  }, []);
  const t = useCallback((source: string, vars?: Record<string, string | number>) => translate(dictionary, source, vars), [dictionary]);
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n outside I18nProvider');
  return value;
}

/** API error codes shown to people. */
export function errorText(t: I18n['t'], error: unknown): string {
  const code = error instanceof ApiError ? error.code : 'server_error';
  const messages: Record<string, string> = {
    invalid_credentials: 'Prisijungti nepavyko. Patikrinkite duomenis.',
    too_many_attempts: 'Per daug bandymų. Bandykite vėliau.',
    company_pending: 'Jūsų paskyra dar laukia administratoriaus patvirtinimo.',
    company_suspended: 'Jūsų paskyra laikinai sustabdyta. Susisiekite su mumis.',
    company_rejected: 'Jūsų registracija nepatvirtinta.',
    email_taken: 'Šis el. pašto adresas jau užregistruotas.',
    invalid: 'Patikrinkite pažymėtus laukus.',
    current_password_wrong: 'Dabartinis slaptažodis neteisingas.',
    password_unchanged: 'Naujas slaptažodis turi skirtis nuo dabartinio.',
    not_signed_in: 'Prisijunkite iš naujo.',
    not_found: 'Įrašas nerastas.',
    csrf: 'Puslapis pasenęs. Atnaujinkite jį.',
    read_only_view: 'Administratoriaus peržiūra: pakeitimai negalimi.',
    confirm_link_invalid: 'Nuoroda nebegalioja arba jau panaudota.',
    owner_only: 'Tai gali atlikti tik paskyros savininkas.',
    billing_unavailable: 'Mokėjimai šiuo metu nepasiekiami. Bandykite vėliau.',
    already_subscribed: 'Prenumerata jau aktyvi.',
    not_subscribed: 'Prenumeratos dar nėra.',
    plan_free: 'Jūsų planas nemokamas.',
    plan_by_agreement: 'Šio plano kaina — pagal susitarimą. Parašykite mums.',
    consent_required: 'Reikia savininko leidimo. Paprašykite prieigos — savininkas gaus el. laišką.',
    view_request_open: 'Prašymas jau išsiųstas ir laukia savininko sprendimo.',
    request_not_open: 'Šis prašymas jau nebegalioja arba sprendimas jau priimtas.',
    vat_code_not_allowed: 'Ši PVM klasifikacija šiai sąskaitai netinka.',
    vat_code_unknown: 'Nežinoma PVM klasifikacija.',
    number_taken: 'Toks sąskaitos numeris jau yra. Patikrinkite šablono kitą numerį.',
    project_mail_not_configured: 'Projekto el. pašto siuntimas nenustatytas (Projektai → SMTP).',
    mail_failed: 'Laiško išsiųsti nepavyko. Patikrinkite projekto SMTP nustatymus.',
    recurring_date_past: 'Kitos sąskaitos data negali būti praeityje.',
    payments_not_enabled: 'Apmokėjimas internetu neįjungtas. Prijunkite Stripe, Paysera arba Swedbank skiltyje Pinigai → Apmokėjimas internetu.',
    swedbank_key_invalid: 'Raktas netinka: reikia RSA (bent 2048 bitų) arba EC P-256 privataus rakto PEM formatu, arba sukurkite raktą čia.',
    payments_unavailable: 'Banko paslauga šiuo metu nepasiekiama. Bandykite po kelių minučių.',
    payments_not_configured: 'Apmokėjimas internetu šiame serveryje dar neįjungtas.',
    invoice_paid: 'Ši sąskaita jau apmokėta.',
    quote_invoiced: 'Pagal šį pasiūlymą jau išrašyta sąskaita; jo keisti nebegalima.',
    quote_valid_before_issue: 'Pasiūlymo galiojimo data negali būti ankstesnė už jo datą.',
    recurring_end_before_start: 'Pabaiga negali būti anksčiau už kitą sąskaitą.',
    recurring_email_required: 'Automatiniam siuntimui reikia kliento el. pašto.',
    invalid_lines: 'Patikrinkite sąskaitos eilutes.',
    read_only_role: 'Jūsų teisės — tik peržiūra. Pakeitimų atlikti negalite.',
    account_exists: 'Sąskaita su tokiu kodu jau yra.',
    account_no_parent: 'Subsąskaitos kodas turi prasidėti esamos sąskaitos kodu (pvz., 2711 po 271).',
    account_in_use: 'Ši sąskaita naudojama operacijoms. Pirmiausia pasirinkite kitą.',
    account_has_children: 'Pirmiausia ištrinkite subsąskaitas.',
    account_standard: 'Pavyzdinio plano sąskaitos ištrinti negalima — ją galima paslėpti.',
    account_hidden: 'Pasirinkta sąskaita paslėpta.',
    account_not_postable: 'Pasirinkite sąskaitą be subsąskaitų.',
    account_wrong_class: 'Šiai operacijai netinka tokios klasės sąskaita.',
    account_not_found: 'Sąskaita nerasta.',
    entry_not_balanced: 'Debetas ir kreditas turi sutapti.',
    period_locked: 'Šis laikotarpis uždarytas. Pirmiausia jį atidarykite Didžiojoje knygoje.',
    before_books_start: 'Data ankstesnė už apskaitos pradžią.',
    books_locked: 'Kol laikotarpis uždarytas, apskaitos pradžios keisti negalima.',
    already_locked: 'Knygos jau uždarytos iki šios dienos ar vėliau.',
    pending_in_period: 'Laikotarpyje liko neįtrauktų operacijų (žr. Žurnalą). Pirmiausia jas sutvarkykite.',
    not_locked: 'Šis laikotarpis neuždarytas.',
    books_start_required: 'Pirmiausia nurodykite apskaitos pradžią.',
    payroll_not_approved: 'Pirmiausia patvirtinkite mėnesio atlyginimus.',
    not_vat_payer: 'Įmonė nėra PVM mokėtoja.',
    fr0600_unmapped: 'Kai kurių PVM kodų programa nepriskiria FR0600 laukeliams.',
    nothing_to_declare: 'Tą mėnesį patvirtintų atlyginimų neišmokėta – deklaruoti nėra ko.',
    company_code_missing: 'Įmonės rekvizituose įrašykite įmonės kodą.',
    sodra_code_missing: 'Mokesčių nustatymuose įrašykite Sodros draudėjo kodą.',
    personal_code_missing: 'Ne visiems darbuotojams įrašytas asmens kodas (Darbuotojai → redaguoti).',
    sdup_group_missing: 'Ne visiems darbuotojams įrašyta pareigybių grupė (Darbuotojai → redaguoti).',
    sdup_gender_missing: 'Ne visiems darbuotojams žinoma lytis: įrašykite asmens kodą arba lytį (Darbuotojai → redaguoti).',
    opening_exists: 'Įžanginiai likučiai jau įvesti.',
    file_unreadable: 'Failo nepavyko perskaityti. Įkelkite .xlsx arba .csv failą.',
    file_required: 'Pasirinkite failą.',
    file_too_large: 'Failas per didelis.',
    line_one_side: 'Kiekvienoje eilutėje — arba debetas, arba kreditas.',
    already_member: 'Šis žmogus jau yra įmonės komandoje.',
    last_owner: 'Įmonėje turi likti bent vienas savininkas.',
    remove_self: 'Savęs pašalinti negalite. Naudokite „Palikti įmonę“.',
    too_many_invites: 'Per daug laukiančių kvietimų. Atšaukite nereikalingus.',
    fx_unavailable: 'Lietuvos banko kurso gauti nepavyko. Įrašykite kursą patys.',
    currency_unknown: 'Šios valiutos kurso nėra.',
    cash_entry_of_z_report: 'Šis orderis sukurtas iš Z ataskaitos — pašalinkite Z ataskaitą.',
    z_report_exists: 'Tokia Z ataskaita jau įvesta.',
    z_payments_exceed_total: 'Grynaisiais ir kortele daugiau nei iš viso.',
    z_vat_mismatch: 'PVM eilučių suma nesutampa su bendra suma.',
    payroll_exists: 'Šio mėnesio atlyginimai jau paruošti.',
    payroll_approved: 'Atlyginimai patvirtinti — pirmiausia atšaukite patvirtinimą.',
    payroll_year_unknown: 'Šių metų mokesčių tarifai programoje dar nenustatyti.',
    no_employees: 'Šį mėnesį dirbančių darbuotojų nėra.',
    employee_has_payroll: 'Darbuotojas jau turi atlyginimų — nurodykite darbo pabaigos datą.',
    client_has_owner: 'Ši įmonė jau turi savininką.',
    firm_link_requested: 'Pirmiausia priimkite kliento prašymą.',
    firm_has_clients: 'Kol turite klientų, Firm planas lieka. Pirmiausia nutraukite ryšius su klientais.',
    not_on_trial: 'Planą be kortelės galima išbandyti tik bandomuoju laikotarpiu.',
    plan_not_for_company: 'Šis planas netinka jūsų veiklos formai.',
    not_a_firm: 'Buhalterio panelė — tik Firm plano įmonėms.',
    firm_not_found: 'Buhalterinė įmonė su tokiu kodu nerasta (ji turi naudoti FakturaSmart Pro Firm planą).',
    firm_link_exists: 'Jūsų įmonė jau turi buhalterinę įmonę arba prašymas jau išsiųstas.',
    firm_link_active: 'Šis klientas jau patvirtintas.',
    firm_member: 'Buhalterinės įmonės žmonės išeina, kai nutraukiamas ryšys su įmone.',
    demo_disabled: 'Demo versijoje tai išjungta.',
    demo_limit: 'Pasiekta demo riba. Demo bus atkurta per valandą — tada galėsite tęsti.',
    demo_suspended: 'Demo laikinai išjungta. Bandykite vėliau.',
    addon_needs_subscription: 'Kredito papildymas kortele dar neįjungtas. Parašykite mums.',
    premium_required: 'Tai yra Premium plane. Pasirinkite planą: Nustatymai → Planas.',
    basic_required: 'Tai yra Basic ir Premium planuose. Pasirinkite planą: Nustatymai → Planas.',
    free_invoice_limit: 'Nemokamame plane — iki 5 sąskaitų per mėnesį. Daugiau sąskaitų — Basic plane.',
    join_invalid: 'Neteisingas įmonės prisijungimo vardas arba slaptažodis, arba prisijungimas uždarytas.',
    join_login_taken: 'Toks įmonės prisijungimo vardas jau užimtas.',
    join_password_required: 'Įjungiant prisijungimą reikia nustatyti įmonės slaptažodį.',
    identity_taken: 'Ši Apple ar Google paskyra jau susieta su kita FakturaSmart Pro paskyra.',
    last_sign_in_method: 'Tai vienintelis būdas prisijungti. Pirmiausia nustatykite slaptažodį arba susiekite kitą būdą.',
    transfer_ownership_first: 'Esate vienintelis įmonės savininkas, o įmonėje yra kitų žmonių. Pirmiausia paskirkite kitą savininką.',
    oauth_not_configured: 'Šis prisijungimo būdas dar neįjungtas.',
    oauth_invalid: 'Prisijungti nepavyko. Bandykite dar kartą.',
    oauth_unavailable: 'Apple ar Google šiuo metu nepasiekiami. Bandykite po kelių minučių.',
    expired: 'Prisijungimas užtruko per ilgai. Bandykite dar kartą.',
    signup_expired: 'Registracijos laikas baigėsi. Prisijunkite su Apple ar Google dar kartą.',
    email_required: 'Reikia el. pašto adreso. Leiskite Apple ar Google jį perduoti.',
    invite_email_mismatch: 'Kvietimas skirtas kitam el. paštui. Prisijunkite su paskyra, kurios el. paštas sutampa su kvietimu.',
    user_limit: 'Jūsų plane daugiau vartotojų nėra vietos (Basic: 1 vartotojas ir buhalteris peržiūrai; Premium: iki 5).',
    contract_required: 'Pirmiausia pasirašykite ir įkelkite sutartį (Nustatymai → Sutartis).',
    contract_not_ready: 'Sutartis dar ruošiama.',
    signed_file_type: 'Įkelkite pasirašytą PDF arba ADOC failą.',
    contract_accepted: 'Sutartis jau priimta.',
    ai_credit_exhausted: 'Šio mėnesio AI kreditas baigėsi. Papildykite 5 € arba palaukite kito mėnesio.',
    ai_unavailable: 'AI asistentas šiuo metu nepasiekiamas. Bandykite vėliau.',
    addon_disabled: 'Šis priedas neįjungtas.',
    ai_key_invalid: 'Anthropic API anahtarı geçersiz veya süresi dolmuş. console.anthropic.com içindeki API Keys bölümünden yeni bir anahtar oluşturun.',
    ai_key_billing: 'Anthropic API fatura ayarlarında sorun var. Claude Console içindeki Billing bölümünü kontrol edin.',
    ai_key_forbidden: 'Bu Anthropic API anahtarının Models API erişim izni yok. Claude Console içindeki çalışma alanı izinlerini kontrol edin.',
    ai_key_check_failed: 'Anthropic API anahtarı doğrulanamadı. Biraz sonra tekrar deneyin; sorun sürerse API bağlantısını kontrol edin.',
    cash_amount_below_invoice: 'Suma mažesnė už skolą — dalinis mokėjimas sąskaitos apmokėta nepažymi.',
    credit_currency_mismatch: 'Kreditinė sąskaita turi būti tos pačios valiutos kaip koreguojama.',
    recurring_date_issued: 'Šiai datai sąskaita jau išrašyta. Atnaujinkite puslapį ir pasirinkite vėlesnę datą.',
    contract_not_uploaded: 'Sutartis dar neįkelta.',
    smtp_password_missing: 'SMTP slaptažodis neįvestas.',
    smtp_host_not_allowed: 'Šis adresas neleidžiamas (vidinis tinklas).',
    smtp_port_not_allowed: 'Leidžiami tik SMTP prievadai: 25, 465, 587, 2525.',
    smtp_host_not_found: 'Serveris nerastas — patikrinkite SMTP serverio adresą.',
    invoice_not_open: 'Ši sąskaita jau apmokėta arba neišsiųsta.',
    incoming_duplicate: 'Ši tiekėjo sąskaita (ta pati serija ir numeris) jau įrašyta. Antrą kartą jos įrašyti nereikia – dokumentą galite atidėti.',
    allocation_amount_invalid: 'Suma per didelė: ji negali viršyti to, kas liko sąskaitoje ir mokėjime.',
    currency_mismatch: 'Mokėjimo ir sąskaitos valiutos skiriasi.',
    payment_fully_linked: 'Šis mokėjimas jau visas susietas.',
    category_direction: 'Ši priežastis netinka šios krypties mokėjimui.',
    note_required: 'Įrašykite pastabą.',
    act_already_answered: 'Į šį aktą jau atsakyta.',
    rule_needs_counterparty: 'Mokėjime nėra gavėjo ar mokėtojo — taisyklės sukurti negalima.',
    vat_code_changes_amounts: 'Ši klasifikacija pakeistų sąskaitos sumas; išsiųstai sąskaitai tai negalima (išrašykite kreditinę).',
  };
  return t(messages[code] ?? 'Veiksmo atlikti nepavyko. Bandykite dar kartą.');
}
